import React from "react";
import { useTranslation } from "react-i18next";
import { ChartLine } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  REVENUE_CHART_CARD_ICON_COLOR,
  REVENUE_STACKED_AREA_CHART_HEIGHT,
} from "./constants";
import { formatRevenueCurrency } from "./revenueCurrency";
import type { StackedRevenuePoint } from "./mockData";
import {
  AnalyticsAreaFillDefs,
  analyticsAreaFillUrl,
  useAnalyticsChartInstanceId,
} from "../shared/chartAreaFill";
import { formatAnalyticsDayLabel } from "../shared/analyticsFormatting";
import { RevenueChartCard } from "./RevenueChartCard";
import { RevenueChartLegend } from "./RevenueChartLegend";

type SeriesKey = keyof Pick<
  StackedRevenuePoint,
  | "guestServices"
  | "guesteriaPartners"
  | "externalServices"
  | "app"
  | "whatsapp"
>;

type SeriesDefinition = {
  key: SeriesKey;
  label: string;
  color: string;
};

interface RevenueStackedAreaChartProps {
  title: string;
  data: StackedRevenuePoint[];
  series: SeriesDefinition[];
  currency: string;
  showLegend?: boolean;
  chartHeight?: number;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

function StackedRevenueTooltip({
  active,
  payload,
  label,
  series,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string; value?: number; color?: string }>;
  label?: string;
  series: SeriesDefinition[];
  currency: string;
}) {
  const { t } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }

  const total = payload.reduce((sum, item) => sum + Number(item.value ?? 0), 0);
  const seriesByKey = Object.fromEntries(
    series.map((item) => [item.key, item]),
  );

  return (
    <div className="grid min-w-[10rem] gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <p className="font-medium text-zinc-900">
        {formatAnalyticsDayLabel(label)}
      </p>
      {payload.map((item) => {
        const config = seriesByKey[String(item.dataKey)];
        if (!config) {
          return null;
        }

        return (
          <div
            key={config.key}
            className="flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: config.color }}
              />
              <span className="text-muted-foreground">{config.label}</span>
            </div>
            <span className="font-medium text-zinc-900">
              {formatRevenueCurrency(Number(item.value ?? 0), currency)}
            </span>
          </div>
        );
      })}
      <div className="flex items-center justify-between border-t border-border/50 pt-1.5 font-medium text-zinc-900">
        <span>{t("analytics.common.total", "Total")}</span>
        <span>{formatRevenueCurrency(total, currency)}</span>
      </div>
    </div>
  );
}

export const RevenueStackedAreaChart: React.FC<
  RevenueStackedAreaChartProps
> = ({
  title,
  data,
  series,
  currency,
  showLegend = false,
  chartHeight = REVENUE_STACKED_AREA_CHART_HEIGHT,
  onExport,
  exportDisabled,
}) => {
  const chartConfig = series.reduce<ChartConfig>((config, item) => {
    config[item.key] = {
      label: item.label,
      color: item.color,
    };
    return config;
  }, {});

  const maxValue = data.reduce((max, point) => {
    const pointTotal = series.reduce(
      (sum, item) => sum + Number(point[item.key] ?? 0),
      0,
    );
    return Math.max(max, pointTotal);
  }, 0);
  const yAxisMax = Math.ceil(maxValue / 1000) * 1000 || 5000;
  const chartInstanceId = useAnalyticsChartInstanceId();

  return (
    <RevenueChartCard
      title={title}
      icon={<ChartLine />}
      iconColor={REVENUE_CHART_CARD_ICON_COLOR}
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <div className="flex flex-col gap-3">
        <ChartContainer
          config={chartConfig}
          className="w-full !aspect-auto"
          style={{ height: chartHeight }}
        >
          <AreaChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              <AnalyticsAreaFillDefs
                seriesKeys={series.map((item) => item.key)}
                chartInstanceId={chartInstanceId}
              />
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={4}
              className="text-xs text-muted-foreground"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={56}
              domain={[0, yAxisMax]}
              tickFormatter={(value) =>
                formatRevenueCurrency(Number(value), currency)
              }
              className="text-xs text-muted-foreground"
            />
            <ChartTooltip
              cursor={false}
              content={
                <StackedRevenueTooltip series={series} currency={currency} />
              }
            />
            {series.map((item) => (
              <Area
                key={item.key}
                type="monotone"
                dataKey={item.key}
                stackId="revenue"
                stroke={`var(--color-${item.key})`}
                fill={analyticsAreaFillUrl(item.key, chartInstanceId)}
                strokeWidth={1.5}
              />
            ))}
          </AreaChart>
        </ChartContainer>
        {showLegend ? <RevenueChartLegend items={series} /> : null}
      </div>
    </RevenueChartCard>
  );
};
